import { DownloadOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Row, Table } from "antd";
import { useFormik } from "formik";
import { get, toLower } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { buildQueryString, exportFile, showError, showSuccess } from "../../../utils/utilities";
import Button from "../../../components/functional_components/Button/PFButton";
import Filter from "../../../components/functional_components/Filter/Filter";
import { setFilters } from "../../../redux/ducks/Filters";
import { setLoading } from "../../../redux/ducks/Loading";
import User from "../../../services/api/User";
import { dbDate, langDate, paginationDefaults } from "../../../utils/properties";
import dayjs from "dayjs";

const UserSuppliersList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      pageSize: paginationDefaults.size,
      totalItems: paginationDefaults.total,
    },
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {
      sort: "supplier.name;asc",
    });
    dispatch(setFilters(queryString));
    getSuppliersList(queryString.string);
  }, []);

  const columns = [
    { title: t("userSupplier.id"), dataIndex: "id", key: "id" },
    { title: t("userSupplier.name"), dataIndex: "name", key: "name" },
    {
      title: t("userSupplier.dateActivityStart"),
      dataIndex: "dateActivityStart",
      key: "dateActivityStart",
      responsive: ["md"],
      render: (dateStart) => {
        return dateStart !== null ? dayjs(dateStart, dbDate).format(langDate[toLower(currentLanguage)]) : "-";
      },
    },
    {
      title: t("userSupplier.dateActivityEnd"),
      dataIndex: "dateActivityEnd",
      key: "dateActivityEnd",
      responsive: ["md"],
      render: (dateEnd) => {
        return dateEnd !== null ? dayjs(dateEnd, dbDate).format(langDate[toLower(currentLanguage)]) : "-";
      },
    },
    {
      title: t("general.actions"),
      dataIndex: "",
      key: "x",
      width: 80,
      render: (record) => {
        return (
          <div className="space-around">
            <div onClick={() => goToSupplierDetail(record)}>
              <EditOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const goToSupplierDetail = (selectedSupplier) => {
    navigate(`${selectedSupplier ? selectedSupplier.username : "new-supplier"}`);
  };

  const getSuppliersList = async (queryString) => {
    dispatch(setLoading(true));
    let suppliersList = await User.getUserSuppliers(queryString);
    if (!suppliersList.error && suppliersList.data) {
      suppliersList.data.forEach((currentSupplier, idx) => {
        currentSupplier.key = idx;
        currentSupplier.id = currentSupplier.supplier.id;
        currentSupplier.name = currentSupplier.supplier.name;
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: suppliersList.data,
        pagination: {
          currentPage: suppliersList.currentPage ? suppliersList.currentPage + 1 : paginationDefaults.page,
          pageSize: get(suppliersList, "pageSize", paginationDefaults.size),
          totalItems: get(suppliersList, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(suppliersList, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const exportSupplierUsersCsv = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await User.exportSupplierUsersCsv();
    if (!exportResponse.error) {
      const blob = new Blob([exportResponse]);
      exportFile(blob, "suppliers.csv");
      showSuccess();
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  const formikFilter = useFormik({
    initialValues: {
      supplierName: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      values["page"] = 0;
      values["supplier.name"] = values.supplierName;
      const queryString = buildQueryString(filters.obj, values);
      dispatch(setFilters(queryString));
      getSuppliersList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "supplierName",
      name: "supplierName",
      value: formikFilter.values.supplierName,
      label: t("userSupplier.name"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString(
      { page: paginationDefaults.page - 1, size: paginationDefaults.size, sort: "supplier.name;asc" },
      {}
    );
    dispatch(setFilters(queryString));
    getSuppliersList(queryString.string);
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          className="mr-10"
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => goToSupplierDetail()}
        />
        <Button
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={() => exportSupplierUsersCsv()}
        />
      </Row>
      <Table
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.pageSize,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            getSuppliersList(queryString.string);
          },
        }}
        columns={columns}
        dataSource={state.tableContent}
        size="small"
      />
    </>
  );
};

export default UserSuppliersList;
